import type { Event } from "@sentry/react";

// Browser-native fetch network failures, as wrapped by postgrest-js into
// `${error.name}: ${error.message}` (Chrome / Firefox / Safari respectively).
// Sentry may append the target host, so match anywhere in the message.
const NETWORK_ERROR_PATTERNS = [
  /\bFailed to fetch\b/i,
  /\bNetworkError when attempting to fetch resource\b/i,
  /\bLoad failed\b/,
];

/**
 * True when a Sentry event is a transient client-side network failure
 * captured by @supabase/sentry-js-integration (WEXARE-MVP-Y).
 *
 * The integration reports every PostgREST `res.error` as an exception,
 * including pure connectivity failures (offline, flaky network, aborted
 * navigation) that app code already handles and that aren't actionable.
 * Scoped to events carrying the integration's `supabase` context so
 * network errors from other sources (e.g. a CORS misconfiguration on a
 * different endpoint) still report.
 */
export function isSupabaseTransientNetworkError(event: Event): boolean {
  if (!event.contexts?.supabase) return false;
  const value = event.exception?.values?.[0]?.value ?? "";
  return NETWORK_ERROR_PATTERNS.some((p) => p.test(value));
}

/**
 * True when a Sentry event is a PostgREST "JWT expired" error (PGRST301)
 * captured by @supabase/sentry-js-integration (WEXARE-MVP-1P).
 *
 * These are a normal part of the token lifecycle: the custom JWT has a
 * 1-hour TTL, and edge cases (tab sleep, clock skew, in-flight requests
 * at the expiry boundary) cause a small fraction to arrive after expiry.
 * The fetch wrapper retries them transparently, so they are not
 * user-visible and not actionable.
 */
export function isSupabaseJwtExpiredError(event: Event): boolean {
  if (!event.contexts?.supabase) return false;
  const value = event.exception?.values?.[0]?.value ?? "";
  return /\bJWT expired\b/i.test(value);
}
